import { useState } from "react";
import {
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  AlertTriangle,
  Image as ImageIcon,
  RefreshCw,
} from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loading } from "@/components/Loading";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAwaitingValidationServiceOrders,
  useValidateServiceOrder,
  useRejectServiceOrderEvaluation,
} from "@/hooks/useServiceOrders";
import { formatDate } from "@/lib/utils";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { ServiceOrderDetails } from "@/pages/service-ordens/view";
// Tipos
interface WorkOrderItem {
  id: string;
  occurrence?: {
    id: string;
    protocol: string;
    category?: { name: string };
    subcategory?: { name: string };
    completionMedia?: Array<{
      id: string;
      key: string;
    }>;
  };
}

interface WorkOrder {
  id: string;
  workOrderNumber: string;
  title: string;
  description?: string;
  priority: string;
  status: string;
  assignedTeam?: { name: string };
  sector?: { name: string };
  items?: WorkOrderItem[];
  createdAt: string;
  updatedAt: string;
}

interface MediaSlide {
  src: string;
  alt: string;
}

const priorityLabels: Record<string, { label: string; color: string }> = {
  LOW: { label: "Baixa", color: "bg-blue-100 text-blue-700" },
  MEDIUM: { label: "Média", color: "bg-yellow-100 text-yellow-700" },
  HIGH: { label: "Alta", color: "bg-orange-100 text-orange-700" },
  URGENT: { label: "Crítica", color: "bg-red-100 text-red-700" },
};

export const WorkOrderValidation = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const { data, isLoading, refetch } = useGetAwaitingValidationServiceOrders({
    page: currentPage,
    limit: 10,
  });

  const validateMutation = useValidateServiceOrder();
  const rejectMutation = useRejectServiceOrderEvaluation();

  const orders = data?.data || [];
  const total = data?.total || 0;

  const handleApprove = (order: WorkOrder) => {
    setSelectedOrder(order);
    setApproveDialogOpen(true);
  };

  const handleReject = (order: WorkOrder) => {
    setSelectedOrder(order);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleViewDetails = (order: WorkOrder) => {
    setSelectedOrder(order);
    setDetailsDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedOrder) return;

    try {
      await validateMutation.mutateAsync(selectedOrder.id);
      toast.success("Ordem de serviço aprovada com sucesso!");
      queryClient.invalidateQueries({
        queryKey: ["getAwaitingValidationServiceOrders"],
      });
      queryClient.invalidateQueries({ queryKey: ["getServiceOrders"] });
      setApproveDialogOpen(false);
      setSelectedOrder(null);
    } catch (error: unknown) {
      console.error("Erro ao aprovar:", error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || "Erro ao aprovar ordem de serviço");
    }
  };

  const confirmReject = () => {
    if (!selectedOrder) return;

    if (rejectReason.length < 10) {
      toast.error("A justificativa deve ter no mínimo 10 caracteres");
      return;
    }

    if (rejectReason.length > 1000) {
      toast.error("A justificativa deve ter no máximo 1000 caracteres");
      return;
    }
    rejectMutation.mutate(
      {
        id: selectedOrder.id,
        reason: rejectReason,
      },
      {
        onSuccess: () => {
          toast.success("Ordem de serviço rejeitada. A equipe foi notificada.");
          queryClient.invalidateQueries({
            queryKey: ["getAwaitingValidationServiceOrders"],
          });
          queryClient.invalidateQueries({ queryKey: ["getServiceOrders"] });
        },
        onError: (error: unknown) => {
          console.error("Erro ao rejeitar:", error);
          const axiosError = error as { response?: { data?: { message?: string } } };
          toast.error(axiosError.response?.data?.message || "Erro ao rejeitar ordem de serviço");
        },
        onSettled: () => {
          setRejectDialogOpen(false);
          setSelectedOrder(null);
          setRejectReason("");
          rejectMutation.reset();
        },
      }
    );
  };

  // Preparar imagens para o lightbox
  const getCompletionMedia = (order: WorkOrder | null): MediaSlide[] => {
    if (!order?.items) return [];
    const media: MediaSlide[] = [];
    order.items.forEach((item: WorkOrderItem) => {
      if (item.occurrence?.completionMedia) {
        item.occurrence.completionMedia.forEach((m) => {
          media.push({
            src: `${import.meta.env.VITE_BUCKET_URL}${m.key}`,
            alt: m.key?.split("/").pop() || "Imagem",
          });
        });
      }
    });
    return media;
  };

  const openLightbox = (order: WorkOrder, index: number) => {
    setSelectedOrder(order);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <HeaderPage
        title="Validação de Ordens de Serviço"
        description="Analise e valide as ordens de serviço aguardando aprovação"
      />

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 mb-6">
        <Card className="bg-amber-50 border-amber-200">
          <CardContentUI className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">
                  Aguardando Validação
                </p>
                <p className="text-2xl font-bold text-amber-700">{total}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContentUI>
        </Card>

        <Card className="bg-white">
          <CardContentUI className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">
                  Última Atualização
                </p>
                <p className="text-lg font-semibold">
                  {new Date().toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                className="cursor-pointer"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardContentUI>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContentUI className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">
                  Atualização Automática
                </p>
                <p className="text-lg font-semibold text-blue-700">30 segundos</p>
              </div>
              <RefreshCw className="h-8 w-8 text-blue-500" />
            </div>
          </CardContentUI>
        </Card>
      </div>

      <CardContent
        title="Ordens Aguardando Validação"
        content={
          isLoading ? (
            <Loading label="Carregando ordens de serviço..." />
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <CheckCircle className="h-12 w-12 mb-4 text-green-400" />
              <p className="text-lg font-medium">Nenhuma ordem aguardando validação</p>
              <p className="text-sm">Todas as ordens foram processadas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº da OS</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Prioridade</TableHead>
                    <TableHead>Equipe</TableHead>
                    <TableHead>Ocorrências</TableHead>
                    <TableHead>Submetido em</TableHead>
                    <TableHead className="text-center">Mídias</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order: WorkOrder) => {
                    const completionMedia = getCompletionMedia(order);
                    return (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="border-b hover:bg-gray-50"
                      >
                        <TableCell className="font-medium">
                          {order.workOrderNumber}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            <p className="truncate font-medium">{order.title}</p>
                            {order.description && (
                              <p className="text-xs text-gray-500 truncate">
                                {order.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              priorityLabels[order.priority]?.color ||
                              "bg-gray-100"
                            }
                          >
                            {priorityLabels[order.priority]?.label ||
                              order.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">
                              {order.assignedTeam?.name || "Sem equipe"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {order.items?.length || 0} ocorrência(s)
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Calendar className="h-4 w-4" />
                            {formatDate(order.updatedAt)}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {completionMedia.length > 0 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="cursor-pointer"
                              onClick={() => openLightbox(order, 0)}
                            >
                              <ImageIcon className="h-4 w-4 mr-1" />
                              {completionMedia.length}
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 cursor-pointer"
                              title="Ver Detalhes"
                              onClick={() => handleViewDetails(order)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 cursor-pointer text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Aprovar"
                              onClick={() => handleApprove(order)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Rejeitar"
                              onClick={() => handleReject(order)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Paginação */}
              {total > 10 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <p className="text-sm text-gray-500">
                    Mostrando {orders.length} de {total} ordens
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage * 10 >= total}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        }
      />

      {/* Dialog de Detalhes - Página Completa da OS */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent 
          className="!max-w-[90vw] w-full h-[90vh] p-0 overflow-hidden flex flex-col"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {selectedOrder && (
              <ServiceOrderDetails idProp={selectedOrder.id} />
            )}
          </div>
          {/* Rodapé fixo com botões de ação */}
          <div className="border-t bg-white p-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDetailsDialogOpen(false)}
            >
              Fechar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setDetailsDialogOpen(false);
                if (selectedOrder) handleReject(selectedOrder);
              }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Rejeitar
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                setDetailsDialogOpen(false);
                if (selectedOrder) handleApprove(selectedOrder);
              }}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Aprovar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Aprovação */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Aprovar Ordem de Serviço
            </DialogTitle>
            <DialogDescription>
              Você está prestes a aprovar a OS{" "}
              <strong>{selectedOrder?.workOrderNumber}</strong>. Esta ação irá:
            </DialogDescription>
          </DialogHeader>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>Marcar a ordem de serviço como concluída</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>Resolver as ocorrências vinculadas</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>Enviar notificações aos cidadãos</span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApproveDialogOpen(false)}
              disabled={validateMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={confirmApprove}
              disabled={validateMutation.isPending}
            >
              {validateMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Aprovando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmar Aprovação
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Rejeição */}
      <Dialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        modal={true}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Rejeitar Ordem de Serviço
            </DialogTitle>
            <DialogDescription>
              Você está prestes a rejeitar a OS{" "}
              <strong>{selectedOrder?.workOrderNumber}</strong>. A ordem
              retornará para status "Em Andamento" e a equipe será notificada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">Atenção</p>
                  <p>
                    Forneça uma justificativa clara para que a equipe possa
                    corrigir o problema.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rejectReason">
                Justificativa da Rejeição{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="rejectReason"
                placeholder="Descreva o motivo da rejeição e o que precisa ser corrigido..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-gray-500">
                {rejectReason.length}/1000 caracteres (mínimo 10)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              disabled={rejectMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmReject}
              disabled={rejectMutation.isPending || rejectReason.length < 10}
            >
              {rejectMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Rejeitando...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Confirmar Rejeição
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox para visualização de imagens */}
      {selectedOrder && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={getCompletionMedia(selectedOrder)}
          styles={{ 
            container: { backgroundColor: "rgba(0, 0, 0, 0.9)", zIndex: 9999 },
            root: { zIndex: 9999 }
          }}
        />
      )}
    </>
  );
};
